import styled, { css } from "styled-components";

import { getCssConditions } from "../utils";

export interface SetWrapperProps {
  $horizontalAlignment: string;
  $verticalAlignment: string;
  $setsPerLine: number;
  $maxWidth: string;
  $setsSpace: number;
  $totalItems: number;
  $mobileSetsPerLine: number;
  $mobileMediaWidth: number;
}

export const SetWrapperStyled = styled.div.attrs<Record<string, any>>(
  (props) => ({
    "data-mobile-fill-cell": props.$mobileSetsPerLine > 2 ? "true" : undefined,
  }),
)`
  /* Ratio multiplies entity-width in the media formula (Image/Video .styled),
     the same mechanism SideBySide groups use. Below 100% it also flags "capped"
     so the shape cap kicks in. Keeps Array and Group on one consistent path. */
  --combo-mobile-media-ratio: ${({ $mobileMediaWidth }) =>
    $mobileMediaWidth < 100 ? $mobileMediaWidth / 100 : 1};
  --combo-mobile-media-capped: ${({ $mobileMediaWidth }) =>
    $mobileMediaWidth < 100 ? 1 : 0};
  width: ${({ $setsPerLine, $setsSpace }) =>
    `calc(100% / ${$setsPerLine} - ${$setsSpace ?? 16}px +  ${($setsSpace ?? 16) / $setsPerLine}px)`};
  margin-left: ${({ $setsSpace }) => $setsSpace / 2}px;
  margin-right: ${({ $setsSpace }) => $setsSpace / 2}px;
  margin-top: ${({ $setsSpace }) => $setsSpace}px;

  @media screen and (max-width: 768px) {
    --gapX: 16px;
    --gapY: 16px;
    --combo-mobile-fill-cell: ${({ $mobileSetsPerLine }) =>
      $mobileSetsPerLine > 2 ? 1 : 0};
    /* Inter-item spacing is handled by the flex gap on the container
       (.arrayCombo, see ArrayCombo below), not per-item margins. The old
       approach used margins + :nth-child resets to zero the edge margins, but
       those indices assume the cards are the only children of .arrayCombo —
       when a non-card sibling shifted the count, an inner margin got zeroed and
       adjacent cards collapsed to a 0 gap. A container gap always puts exactly
       --gapX between columns regardless of sibling order/count. The width
       formula already bakes in the column gap: (100% - (n-1)*gapX) / n. */
    margin: 0;
    width: ${({ $mobileSetsPerLine }) =>
      `calc(100% / ${$mobileSetsPerLine} - var(--gapX) + (var(--gapX) / ${$mobileSetsPerLine}))`};

    ${({ $mobileSetsPerLine }) =>
      $mobileSetsPerLine > 1 &&
      css`
        /* A set with a background normally bleeds 8px out each side to hug the
           slide edge (see set/styles/index.scss .set[data-has-background]).
           In a multi-column mobile layout that bleed pulls the fill into the
           inter-card gap on both sides, so adjacent cards touch. Keep the fill
           inside its cell here so the 16px column gap survives. */
        .set[data-has-background="true"] {
          margin-left: 0 !important;
          margin-right: 0 !important;
          width: 100% !important;
        }
      `}

    /* Override logo's Tailwind max-md:!tw-w-1/2 (50%) so logo fills cell when 3+ groups per line */
    &[data-mobile-fill-cell="true"] .logoLayout {
      width: 100% !important;
    }
  }

  @media (min-width: 768px) {
    ${({ $setsPerLine, $totalItems }) =>
      getCssConditions($setsPerLine, $totalItems)}
  }

  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
  justify-content: ${({ $horizontalAlignment }) =>
    $horizontalAlignment || "inherit"};
  align-items: ${({ $verticalAlignment }) => $verticalAlignment || "inherit"};
  .set {
    height: 100%;
    .setInnerWrapper {
      height: inherit;
    }
  }
`;

export const ArrayCombo = styled.div`
  &.arrayCombo {
    display: flex;
    align-items: unset !important;
  }

  /* Mobile inter-item spacing lives on the container as flex \`gap\` so it can't
     be broken by nth-child shifts. Values mirror --gapY / --gapX on the
     set-wrapper items: 40px between rows, 16px between columns. */
  @media screen and (max-width: 768px) {
    &.arrayCombo {
      gap: 16px 16px;
    }
  }
`;
